import { useCallback, useEffect, useRef, useState } from "react";

const dbToGain = (db) => 10 ** (db / 20);

async function decode(ctx, url, signal) {
  const res = await fetch(url, { credentials: "include", signal });
  if (!res.ok) throw new Error(`Couldn't load audio (${res.status})`);
  return ctx.decodeAudioData(await res.arrayBuffer());
}

/**
 * Your take over the backing, in the browser and sample-accurate — so the timing nudge you hear is what gets saved.
 * shiftMs > 0 = your voice comes in later.
 */
export default function useLineUpPreview({ musicUrl, extraUrl, vocalUrl, shiftMs, vocalDb, musicDb }) {
  const ctxRef = useRef(null);
  const cache = useRef(new Map());
  const buffers = useRef({});
  const live = useRef(null);
  const [state, setState] = useState("idle");
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const settings = useRef({ shiftMs, vocalDb, musicDb });
  settings.current = { shiftMs, vocalDb, musicDb };

  const stop = useCallback(() => {
    if (live.current) {
      live.current.sources.forEach((s) => { s.onended = null; try { s.stop(); } catch { /* already stopped */ } });
      live.current = null;
    }
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (!musicUrl || !vocalUrl) return undefined;
    const ctrl = new AbortController();
    const ctx = ctxRef.current || (ctxRef.current = new (window.AudioContext || window.webkitAudioContext)());
    const urls = [musicUrl, extraUrl, vocalUrl].filter(Boolean);
    const get = (u) => cache.current.get(u) || decode(ctx, u, ctrl.signal).then((b) => { cache.current.set(u, b); return b; });
    stop();
    setState("loading");
    Promise.all(urls.map(get)).then((bufs) => {
      [...cache.current.keys()].forEach((k) => { if (!urls.includes(k)) cache.current.delete(k); });
      const [music, ...rest] = bufs;
      buffers.current = { music, extra: extraUrl ? rest[0] : null, vocal: rest[rest.length - 1] };
      setDuration(music.duration);
      setState("ready");
    }).catch(() => { if (!ctrl.signal.aborted) setState("error"); });
    return () => ctrl.abort();
  }, [musicUrl, extraUrl, vocalUrl, stop]);

  const play = useCallback((from) => {
    const ctx = ctxRef.current;
    const { music, extra, vocal } = buffers.current;
    if (!ctx || !music || !vocal) return;
    stop();
    ctx.resume();
    const { shiftMs: sh, vocalDb: vd, musicDb: md } = settings.current;
    const at = ctx.currentTime + 0.06;
    const mg = ctx.createGain();
    const vg = ctx.createGain();
    mg.gain.value = dbToGain(md);
    vg.gain.value = dbToGain(vd);
    mg.connect(ctx.destination);
    vg.connect(ctx.destination);
    const src = (buf, out) => { const s = ctx.createBufferSource(); s.buffer = buf; s.connect(out); return s; };
    const sources = [src(music, mg), ...(extra ? [src(extra, mg)] : [])];
    sources.forEach((s) => s.start(at, from));
    const v = src(vocal, vg);
    const vFrom = from - sh / 1000;
    if (vFrom >= 0) v.start(at, vFrom); else v.start(at - vFrom, 0);
    sources[0].onended = () => { live.current = null; setPlaying(false); setTime(0); };
    live.current = { sources: [...sources, v], mg, vg, zero: at - from };
    setPlaying(true);
  }, [stop]);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => { if (live.current) setTime(Math.max(0, ctxRef.current.currentTime - live.current.zero)); }, 200);
    return () => clearInterval(t);
  }, [playing]);

  useEffect(() => {
    if (!live.current) return;
    live.current.mg.gain.value = dbToGain(musicDb);
    live.current.vg.gain.value = dbToGain(vocalDb);
  }, [vocalDb, musicDb]);

  const timeRef = useRef(0);
  timeRef.current = time;
  useEffect(() => { if (live.current) play(timeRef.current); }, [shiftMs, play]);

  useEffect(() => () => { stop(); ctxRef.current?.close?.(); ctxRef.current = null; }, [stop]);

  const toggle = () => (playing ? stop() : play(time >= duration - 0.2 ? 0 : time));
  const seek = (t) => { setTime(t); if (playing) play(t); };
  return { state, playing, time, duration, toggle, seek, stop };
}
